import React, {
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "react-toastify";

import Navbar from "../components/Navbar";
import Sidebar from "../components/Sidebar";
import Loader from "../components/Loader";
import ConfirmModal from "../components/ConfirmModal";
import Pagination from "../components/Pagination";

import authService from "../services/authService";
import shopService from "../services/shopService";
import reportService from "../services/reportService";

const ITEMS_PER_PAGE = 8;

const TransferShop = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [user, setUser] = useState(
    authService.getCurrentUser()
  );

  const [shops, setShops] = useState([]);
  const [transactions, setTransactions] = useState([]);

  const [selectedShopOwnerId, setSelectedShopOwnerId] =
    useState("");

  const [amount, setAmount] = useState("");

  const [loading, setLoading] = useState(true);
  const [transferring, setTransferring] =
    useState(false);

  const [showConfirm, setShowConfirm] =
    useState(false);

  const [currentPage, setCurrentPage] =
    useState(1);

  const toggleSidebar = () => {
    setSidebarOpen((previous) => !previous);
  };

  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  const formatCoins = (value) => {
    return Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return "N/A";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "N/A";
    }

    return date.toLocaleString("en-IN");
  };

  const fetchData = async ({
    silent = false,
  } = {}) => {
    try {
      if (!silent) {
        setLoading(true);
      }

      setUser(authService.getCurrentUser());

      const [shopsResult, transactionsResult] =
        await Promise.allSettled([
          shopService.getShops(),
          reportService.getTransactionReport(),
        ]);

      if (shopsResult.status === "fulfilled") {
        const response = shopsResult.value;

        if (response?.success) {
          setShops(
            Array.isArray(response.shops)
              ? response.shops
              : []
          );
        } else {
          setShops([]);

          toast.error(
            response?.message ||
              "Unable to load Shops."
          );
        }
      } else {
        setShops([]);

        toast.error(
          shopsResult.reason?.response?.data?.message ||
            "Unable to load Shops."
        );
      }

      if (transactionsResult.status === "fulfilled") {
        const response = transactionsResult.value;

        const transactionRows = Array.isArray(
          response?.transactions
        )
          ? response.transactions
          : Array.isArray(response?.data)
            ? response.data
            : [];

        setTransactions(transactionRows);
      } else {
        setTransactions([]);

        console.error(
          "Transaction Report Error:",
          transactionsResult.reason
        );
      }
    } catch (error) {
      console.error(
        "Load Shop Transfer Data Error:",
        error
      );

      toast.error(
        error.response?.data?.message ||
          "Unable to load Shop transfer data."
      );
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const selectedShop = useMemo(() => {
    return shops.find(
      (shop) =>
        String(shop.owner_id) ===
        String(selectedShopOwnerId)
    );
  }, [shops, selectedShopOwnerId]);

  const shopOwnerIds = useMemo(() => {
    return new Set(
      shops
        .map((shop) => Number(shop.owner_id))
        .filter((id) => Number.isInteger(id))
    );
  }, [shops]);

  const shopTransactions = useMemo(() => {
    const loggedInUserId = Number(user?.id);

    return transactions.filter((transaction) => {
      const senderId = Number(
        transaction.sender_id
      );

      const receiverId = Number(
        transaction.receiver_id
      );

      const senderMatches =
        !Number.isInteger(senderId) ||
        senderId === loggedInUserId;

      const receiverMatches =
        !Number.isInteger(receiverId) ||
        shopOwnerIds.has(receiverId);

      const transactionType = String(
        transaction.type || ""
      ).toUpperCase();

      const typeMatches =
        !transactionType ||
        transactionType === "TRANSFER";

      return (
        senderMatches &&
        receiverMatches &&
        typeMatches
      );
    });
  }, [transactions, shopOwnerIds, user?.id]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      shopTransactions.length / ITEMS_PER_PAGE
    )
  );

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentTransactions = useMemo(() => {
    const startIndex =
      (currentPage - 1) * ITEMS_PER_PAGE;

    return shopTransactions.slice(
      startIndex,
      startIndex + ITEMS_PER_PAGE
    );
  }, [shopTransactions, currentPage]);

  const isShopActive = (shop) => {
    if (!shop) {
      return false;
    }

    const shopStatus = String(
      shop.status || "ACTIVE"
    ).toUpperCase();

    const ownerStatus = String(
      shop.owner_status || "ACTIVE"
    ).toUpperCase();

    return (
      shopStatus === "ACTIVE" &&
      ownerStatus === "ACTIVE"
    );
  };

  const updateLoggedInUserWallet = (
    wallet
  ) => {
    const updatedUser = {
      ...(authService.getCurrentUser() || user || {}),
      wallet: Number(wallet),
    };

    authService.setCurrentUser(updatedUser);
    setUser(updatedUser);
  };

  const handleSubmitPre = (event) => {
    event.preventDefault();

    const transferAmount = Number(amount);

    if (!selectedShopOwnerId) {
      toast.error("Please select a Shop.");
      return;
    }

    if (
      !Number.isFinite(transferAmount) ||
      transferAmount <= 0
    ) {
      toast.error(
        "Please enter a valid coin amount."
      );
      return;
    }

    if (!selectedShop) {
      toast.error("Selected Shop was not found.");
      return;
    }

    if (!isShopActive(selectedShop)) {
      toast.error(
        "Coins cannot be transferred to a blocked Shop."
      );
      return;
    }

    const dealerWallet = Number(
      user?.wallet || 0
    );

    if (transferAmount > dealerWallet) {
      toast.error(
        `Insufficient balance. Available wallet: 🪙 ${formatCoins(
          dealerWallet
        )}`
      );
      return;
    }

    setShowConfirm(true);
  };

  const handleConfirmTransfer = async () => {
    const shopOwnerId = Number(
      selectedShopOwnerId
    );

    const transferAmount = Number(amount);

    if (
      !Number.isInteger(shopOwnerId) ||
      shopOwnerId <= 0 ||
      !Number.isFinite(transferAmount) ||
      transferAmount <= 0
    ) {
      toast.error(
        "Invalid Shop or transfer amount."
      );

      setShowConfirm(false);
      return;
    }

    try {
      setTransferring(true);

      const response =
        await shopService.transferToShop(
          shopOwnerId,
          transferAmount
        );

      if (!response?.success) {
        toast.error(
          response?.message ||
            "Unable to transfer coins."
        );
        return;
      }

      const balances = response.balances || {};

      if (balances.dealer_wallet !== undefined) {
        updateLoggedInUserWallet(
          balances.dealer_wallet
        );
      }

      if (balances.shop_wallet !== undefined) {
        setShops((previous) =>
          previous.map((shop) =>
            Number(shop.owner_id) === shopOwnerId
              ? {
                  ...shop,
                  wallet: Number(
                    balances.shop_wallet
                  ),
                }
              : shop
          )
        );
      }

      toast.success(
        response.message ||
          "Coins transferred to Shop successfully."
      );

      setSelectedShopOwnerId("");
      setAmount("");
      setShowConfirm(false);
      setCurrentPage(1);

      await fetchData({
        silent: true,
      });
    } catch (error) {
      console.error(
        "Transfer To Shop Error:",
        error
      );

      toast.error(
        error.response?.data?.message ||
          "Unable to transfer coins to Shop."
      );
    } finally {
      setTransferring(false);
    }
  };

  return (
    <div className="d-flex flex-column min-vh-100 app-shell">
      <Navbar toggleSidebar={toggleSidebar} />

      <div className="d-flex flex-grow-1 position-relative">
        <Sidebar
          isOpen={sidebarOpen}
          closeMobileSidebar={closeSidebar}
        />

        <main className="app-main flex-grow-1 overflow-auto">
          <div className="container-fluid admin-page-wrap">
            <div className="admin-page-header">
              <div>
                <div><span className="admin-page-kicker">WALLET OPERATIONS</span><h2>Transfer Coins to Shop</h2>

                <p>Transfer coins from your Dealer wallet to one of your registered shops.</p></div>
              </div>

              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={() => fetchData()}
                disabled={loading || transferring}
              >
                <i className="bi bi-arrow-clockwise me-2" />
                Refresh
              </button>
            </div>

            <div className="row g-4">
              <div className="col-12 col-xl-5">
                <div className="admin-panel h-100">
                  <div className="admin-panel-head dealer-transfer-head">
                    <h5 className="fw-bold mb-0">
                      <i className="bi bi-arrow-left-right me-2" />
                      Send Coins
                    </h5>
                  </div>

                  <div className="card-body p-4">
                    <div className="p-3 mb-4 rounded-3 bg-light border d-flex justify-content-between align-items-center">
                      <div>
                        <div className="small text-secondary fw-semibold">
                          DEALER WALLET BALANCE
                        </div>

                        <h4 className="fw-bold text-success mb-0">
                          🪙 {formatCoins(user?.wallet)}
                        </h4>
                      </div>

                      <i className="bi bi-wallet2 text-primary fs-1" />
                    </div>

                    {loading ? (
                      <Loader message="Loading Shops..." />
                    ) : (
                      <form onSubmit={handleSubmitPre}>
                        <div className="mb-3">
                          <label
                            htmlFor="shopOwnerId"
                            className="form-label fw-semibold"
                          >
                            Select Shop *
                          </label>

                          <select
                            id="shopOwnerId"
                            className="form-select py-2"
                            value={selectedShopOwnerId}
                            onChange={(event) =>
                              setSelectedShopOwnerId(
                                event.target.value
                              )
                            }
                            disabled={
                              transferring ||
                              shops.length === 0
                            }
                            required
                          >
                            <option value="">
                              {shops.length === 0
                                ? "No Shops available"
                                : "-- Choose Shop --"}
                            </option>

                            {shops.map((shop) => (
                              <option
                                key={shop.id}
                                value={shop.owner_id}
                                disabled={
                                  !isShopActive(shop)
                                }
                              >
                                {shop.shop_name ||
                                  "Unnamed Shop"}{" "}
                                (@
                                {shop.owner_username ||
                                  "unknown"}
                                ) — 🪙{" "}
                                {formatCoins(shop.wallet)}
                                {!isShopActive(shop)
                                  ? " — BLOCKED"
                                  : ""}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedShop && (
                          <div className="alert alert-light border mb-3">
                            <div className="row g-3">
                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Shop
                                </div>

                                <div className="fw-semibold">
                                  {selectedShop.shop_name ||
                                    "N/A"}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Owner
                                </div>

                                <div className="fw-semibold">
                                  {selectedShop.owner_full_name ||
                                    "N/A"}
                                </div>

                                <div className="small text-primary">
                                  @
                                  {selectedShop.owner_username ||
                                    "N/A"}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Current Shop Wallet
                                </div>

                                <div className="fw-bold text-success">
                                  🪙{" "}
                                  {formatCoins(
                                    selectedShop.wallet
                                  )}
                                </div>
                              </div>

                              <div className="col-12 col-sm-6">
                                <div className="small text-secondary">
                                  Address
                                </div>

                                <div className="fw-semibold">
                                  {selectedShop.address ||
                                    "N/A"}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="mb-4">
                          <label
                            htmlFor="transferAmount"
                            className="form-label fw-semibold"
                          >
                            Coin Amount *
                          </label>

                          <div className="input-group">
                            <span className="input-group-text bg-light fw-bold">
                              🪙
                            </span>

                            <input
                              id="transferAmount"
                              type="number"
                              min="0.01"
                              step="0.01"
                              className="form-control py-2"
                              placeholder="Enter transfer amount"
                              value={amount}
                              onChange={(event) =>
                                setAmount(
                                  event.target.value
                                )
                              }
                              disabled={transferring}
                              required
                            />
                          </div>

                          <div className="form-text">
                            Available balance: 🪙{" "}
                            {formatCoins(user?.wallet)}
                          </div>
                        </div>

                        <button
                          type="submit"
                          className="btn btn-primary admin-primary-btn w-100 py-3 fw-bold"
                          disabled={
                            transferring ||
                            !selectedShopOwnerId ||
                            !amount ||
                            Number(amount) <= 0
                          }
                        >
                          <i className="bi bi-send me-2" />
                          Transfer to Shop
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 col-xl-7">
                <div className="admin-panel h-100">
                  <div className="card-header bg-white border-0 p-4 pb-2">
                    <h5 className="fw-bold text-dark mb-1">
                      Shop Transfer Log
                    </h5>

                    <p className="small text-secondary mb-0">
                      Dealer-to-Shop transfer transactions.
                    </p>
                  </div>

                  <div className="card-body p-0">
                    {loading ? (
                      <Loader message="Loading transaction logs..." />
                    ) : currentTransactions.length === 0 ? (
                      <div className="text-center py-5 px-3">
                        <i className="bi bi-receipt fs-1 text-secondary" />

                        <h5 className="mt-3">
                          No Shop Transfers Found
                        </h5>

                        <p className="text-secondary mb-0">
                          Completed transfers will appear here.
                        </p>
                      </div>
                    ) : (
                      <div className="table-responsive">
                        <table className="table admin-table table-hover align-middle mb-0">
                          <thead className="table-light">
                            <tr>
                              <th className="ps-4">
                                ID
                              </th>
                              <th>
                                Shop Receiver
                              </th>
                              <th>Amount</th>
                              <th>Date</th>
                            </tr>
                          </thead>

                          <tbody>
                            {currentTransactions.map(
                              (transaction) => {
                                const receiverShop =
                                  shops.find(
                                    (shop) =>
                                      Number(
                                        shop.owner_id
                                      ) ===
                                      Number(
                                        transaction.receiver_id
                                      )
                                  );

                                return (
                                  <tr
                                    key={
                                      transaction.id ||
                                      `${transaction.receiver_id}-${transaction.created_at}`
                                    }
                                  >
                                    <td className="ps-4 fw-bold">
                                      #
                                      {transaction.id ||
                                        "N/A"}
                                    </td>

                                    <td>
                                      <div className="fw-semibold">
                                        {receiverShop?.shop_name ||
                                          transaction.receiver_username ||
                                          "Shop"}
                                      </div>

                                      <div className="small text-primary">
                                        @
                                        {receiverShop?.owner_username ||
                                          transaction.receiver_username ||
                                          "N/A"}
                                      </div>
                                    </td>

                                    <td className="fw-bold text-success">
                                      🪙{" "}
                                      {formatCoins(
                                        transaction.amount
                                      )}
                                    </td>

                                    <td className="small text-secondary">
                                      {formatDate(
                                        transaction.created_at
                                      )}
                                    </td>
                                  </tr>
                                );
                              }
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {!loading &&
                    shopTransactions.length > 0 && (
                      <div className="card-footer bg-white border-top p-3">
                        <Pagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={setCurrentPage}
                        />
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ConfirmModal
        show={showConfirm}
        title="Confirm Transfer to Shop"
        message={`Transfer 🪙 ${formatCoins(
          amount
        )} coins to ${
          selectedShop?.shop_name || "selected Shop"
        } (@${
          selectedShop?.owner_username || "N/A"
        })?`}
        onConfirm={handleConfirmTransfer}
        onClose={() => {
          if (!transferring) {
            setShowConfirm(false);
          }
        }}
        loading={transferring}
        variant="primary"
      />
    </div>
  );
};

export default TransferShop;